import React, { useEffect, useRef, useState } from "react";

const stats = [
  {
    value: 50000,
    suffix: "+",
    label: "Active Riders",
    sublabel: "Across all cities",
    icon: "🏍️",
    color: "#ffd700",
  },
  {
    value: 12,
    suffix: "",
    label: "Cities Covered",
    sublabel: "And expanding fast",
    icon: "🌍",
    color: "#60a5fa",
  },
  {
    value: 2000000,
    suffix: "+",
    label: "Deliveries Done",
    sublabel: "On time, every time",
    icon: "📦",
    color: "#34d399",
  },
  {
    value: 98,
    suffix: "%",
    label: "Satisfaction Rate",
    sublabel: "Based on 500K+ ratings",
    icon: "⭐",
    color: "#f97316",
  },
];

const testimonials = [
  {
    name: "Amara K.",
    role: "Business Owner, Nairobi",
    text: "OnTheGo cut our delivery costs by 40%. The real-time tracking gives our clients confidence and our team peace of mind.",
    avatar: "AK",
    color: "#ffd700",
  },
  {
    name: "James M.",
    role: "Boda Driver, Kampala",
    text: "I earn KES 8,000 daily on average. The app is simple, payouts are instant, and the support team is always there.",
    avatar: "JM",
    color: "#34d399",
  },
  {
    name: "Fatima O.",
    role: "Regular User, Lagos",
    text: "Fastest food delivery in the city. My order is always hot and the drivers are professional and friendly.",
    avatar: "FO",
    color: "#60a5fa",
  },
];

function useCountUp(target, duration, start) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, animate }) {
  const count = useCountUp(stat?.value, 2200, animate);

  const formatNumber = (n) => {
    if (n >= 1000000) return (n / 1000000)?.toFixed(1) + "M";
    if (n >= 1000) return (n / 1000)?.toFixed(0) + "K";
    return n?.toString();
  };

  return (
    <div
      className="relative p-8 rounded-3xl border overflow-hidden group hover:scale-105 transition-all duration-400 cursor-default"
      style={{
        background: `linear-gradient(135deg, ${stat?.color}10 0%, rgba(10,22,40,0.85) 100%)`,
        borderColor: `${stat?.color}22`,
      }}
    >
      {/* Background icon */}
      <div className="absolute -bottom-2 -right-2 text-7xl opacity-[0.06] select-none pointer-events-none leading-none">
        {stat?.icon}
      </div>
      <div className="relative z-10">
        <div className="text-3xl mb-5">{stat?.icon}</div>
        <div
          className="text-5xl font-black mb-1.5 tabular-nums"
          style={{ color: stat?.color, fontFamily: "'DM Sans', sans-serif" }}
        >
          {formatNumber(count)}{stat?.suffix}
        </div>
        <div className="text-white font-bold text-base mb-1">{stat?.label}</div>
        <div className="text-white/35 text-sm">{stat?.sublabel}</div>
      </div>
    </div>
  );
}

const StatsSection = () => {
  const [animate, setAnimate] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setAnimate(true);
          observer?.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="stats" ref={sectionRef} className="py-24 relative overflow-hidden" style={{ background: "#0a1628" }}>
      {/* Background glow */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{ background: "radial-gradient(ellipse at 50% 0%, #ffd700 0%, transparent 60%)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-5 border"
            style={{ background: "rgba(255,215,0,0.08)", borderColor: "rgba(255,215,0,0.2)" }}
          >
            <span className="text-[#ffd700] text-sm font-semibold">Trusted Across Africa</span>
          </div>
          <h2
            className="text-4xl lg:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Numbers That
            <span className="text-[#ffd700]"> Speak</span>
          </h2>
          <p className="text-white/45 text-lg max-w-xl mx-auto">
            Real impact, real people, real growth — across Africa's fastest-growing cities.
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
          {stats?.map((stat) => (
            <StatCard key={stat?.label} stat={stat} animate={animate} />
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials?.map((t) => (
            <div
              key={t?.name}
              className="p-7 rounded-3xl border transition-all duration-300 hover:border-[#ffd700]/20 group"
              style={{
                background: "rgba(255,255,255,0.03)",
                borderColor: "rgba(255,255,255,0.07)",
              }}
            >
              {/* Quote mark */}
              <div className="text-4xl text-[#ffd700]/20 font-black leading-none mb-4">"</div>
              <p className="text-white/65 text-sm leading-relaxed mb-6 italic">{t?.text}</p>
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center font-black text-[#0a1628] text-xs flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${t?.color}, ${t?.color}bb)` }}
                >
                  {t?.avatar}
                </div>
                <div>
                  <div className="text-white font-bold text-sm">{t?.name}</div>
                  <div className="text-white/35 text-xs">{t?.role}</div>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)]?.map((_, i) => (
                    <span key={i} className="text-[#ffd700] text-xs">★</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
